import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as containers from './containers'
import { AtnDsTableStore } from './store'

/**
 * @deprecated Use the canonical `ds.table.Component` instead.
 */
@ngCore.Component({
  imports: [containers.embedded.Component, containers.widget.Component],
  providers: [AtnDsTableStore],
  selector: 'atn-ds-table-old',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component<
  TTypeParams extends
    ec.models.TypeParams.Constraint = ec.models.TypeParams.Constraint,
> {
  /** {@link ec.Collection Collection} to be displayed in this table component.  */
  // NOTE: only public for 'ngCore.input' purposes
  public readonly _ecSg = ngCore.input.required<ec.Collection<TTypeParams>>({
    alias: 'collection',
  })

  /**
   * The name that will be displayed in the table widget.
   *
   * @default ec.Name
   */
  // NOTE: only public for 'ngCore.input' purposes
  public readonly _nameSg = ngCore.input<string | undefined>('Table', {
    alias: 'name',
  })

  /**
   * Should component be displayed in `embedded` mode, or in `widget` mode.
   *
   * `false` means widget mode.
   *
   * @default false
   */
  // NOTE: only public for 'ngCore.input' purposes
  public readonly _isEmbeddedSg = ngCore.input<boolean | undefined>(false, {
    alias: 'isEmbedded',
  })
  /**
   * Used to control presence of icon that enables us to show column settings.
   *
   * This functionality is only available in {@link _isEmbeddedSg !isEmbedded} mode.
   *
   * @default true
   */
  // NOTE: only public for 'ngCore.input' purposes
  public readonly _showColumnSettingsSg = ngCore.input<boolean | undefined>(
    true,
    {
      alias: 'showColumnSettings',
    },
  )
  /**
   * Display / hide pagination in the table footer.
   *
   * @default false
   */
  // NOTE: only public for 'ngCore.input' purposes
  // TODO: how does this affect actual pagination?
  // - Some ideas, infinite scrolling?
  public readonly _showPaginationSg = ngCore.input<boolean | undefined>(false, {
    alias: 'showPagination',
  })

  /**
   * Controls the entity creation functionality in the table.
   *
   * Supported values:
   *
   * Boolean (true) - Enables creation mode. Clicking the add button
   * creates an empty entity through the editing plugin.
   * Boolean (false)- Disables creation mode. The add button will not be
   * displayed.
   *
   * callback - Enables creation mode with custom handling:
   * - Can process input data through editing plugin
   * - Can execute custom creation logic defined in parent component
   */
  public readonly creationSg = ngCore.input<
    boolean | ((create: (input: TTypeParams['createEntity']) => void) => void)
  >(false, {
    alias: 'creation',
  })

  /**
   * Are rows selectable (have checkboxes).
   *
   * @default false
   */
  // NOTE: only public for 'ngCore.input' purposes
  public readonly _showRowSelectSg = ngCore.input<boolean | undefined>(false, {
    alias: 'showRowSelect',
  })
  /**
   * Are rows selectable (have checkboxes).
   *
   * @default undefined
   */
  // NOTE: only public for 'ngCore.input' purposes
  public readonly _selectionTargetSg = ngCore.input<
    ec.models.Target | undefined
  >(undefined, {
    alias: 'selectionTarget',
  })
  /**
   * Are rows wrapped into multiple lines?
   *
   * @default false
   */
  // NOTE: only public for 'ngCore.input' purposes
  public readonly _isCellContentWrappedSg = ngCore.input<boolean | undefined>(
    false,
    {
      alias: 'wrap',
    },
  )

  /** @see {@link AtnDsTableStore} */
  // NOTE: only public for template purposes
  public readonly _store = ngCore.inject(AtnDsTableStore<TTypeParams>)

  constructor() {
    ngCore.effect(() => {
      this._store.patchState({ ec: this._ecSg() })
    })
    ngCore.effect(() => {
      this._store.patchState({ isEmbedded: this._isEmbeddedSg() })
    })
    ngCore.effect(() => {
      this._store.patchState({
        showColumnSettings: this._showColumnSettingsSg(),
      })
    })
    ngCore.effect(() => {
      this._store.patchState({ showPagination: this._showPaginationSg() })
    })
    ngCore.effect(() => {
      this._store.patchState({
        isCellContentWrapped: this._isCellContentWrappedSg(),
      })
    })
    ngCore.effect(() => {
      this._store.patchState({
        showRowSelect: this._showRowSelectSg(),
      })
    })
    ngCore.effect(() => {
      this._store.patchState({
        selectionTarget: this._selectionTargetSg() ?? ec.models.Target.DEFAULT,
      })
    })
    ngCore.effect(() => {
      this._store.patchState({
        name: this._nameSg(),
      })
    })
    ngCore.effect(() => {
      this._store.patchState({
        creation: this.creationSg(),
      })
    })
  }

  // TODO: we might need to load something into table (on init)
  // ngOnInit() {
  //   currentListPlugin.setInitialParams
  // }
}
